import {
  IBulkPatchPayload,
  IErrorApiResponse,
  IPaginationResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  CrudOptions,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useCrud,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { CommonQueryKeys } from '~/common/api';
import { useApi, useConfig } from '~/common/context';
import {
  CommonErrorCodesEnumType,
  IUserCustomer,
  IUserCustomerPreview,
} from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import { GroupResponse, IUserPayload, IUserResponse } from '~/connect/types';
import { IContainerSupplier } from '~/container-order/types';

const PAGINATE_ALL_LIMIT = 20000; //! workaround for now for

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type IUpdateUserPayload = IUserPayload & { resetPassword: boolean };

type Options = Pick<
  CrudOptions<
    IUserResponse,
    IUserPayload,
    IUpdateUserPayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  getCustomersByUser?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IUserCustomerPreview[]>,
      IErrorApiResponse
    >
  > & {
    args: {
      id: string;
      minLevel?: number;
      parentIds: string[];
    };
  };
  getContainerEligibleCustomersByUser?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IUserCustomerPreview[]>,
      IErrorApiResponse
    >
  > & {
    args: {
      id: string;
      minLevel?: number;
      parentIds: string[];
    };
  };
  getGroups?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<GroupResponse>, IErrorApiResponse>
  >;
  acceptCookieConsent?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    { id: string }
  >;
  acceptTermsOfUse?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    { id: string }
  >;
  unlockUser?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    { id: string }
  >;

  updateLocale?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    { id: string; locale: string }
  >;
  getUserCustomerById?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IUserCustomer>, IErrorApiResponse>
  > & {
    args: { userId: string; customerId: string };
  };
  // Note: This should only be used with specific IDs to prevent performance issues.
  // The purpose is to retrieve customer data in a single request for given IDs.
  paginateCustomersByUser?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<IUserCustomer>, IErrorApiResponse>
  > & {
    args: { ids: string[]; userId: string };
  };
  customersByUserAutocomplete?: { enabled: boolean };
  listSupplierByUser?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IContainerSupplier>>,
      IErrorApiResponse
    >
  > & {
    args: { userId: string };
  };
  bulkPatch?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    IBulkPatchPayload
  >;
};

export const useManageUsers = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    IUserResponse,
    IUserPayload,
    IUpdateUserPayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.users.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/users`,
    },
    {
      ...options,
      update: {
        ...options.update,
        keysToInvalidateOnSuccess: (variables) => [
          ConnectQueryKeys.users.base(),
          CommonQueryKeys.appContext(variables.id),
        ],
      },
      remove: {
        ...options.remove,
        keysToInvalidateOnSuccess: (variables) => [
          ConnectQueryKeys.users.base(),
          CommonQueryKeys.appContext(variables),
        ],
      },
    },
  );

  const getGroups = useQuery({
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getGroups,
    queryKey: CommonQueryKeys.parameters.groups(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<GroupResponse>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/groups`,
      ),
  });

  const customersByUserAutocompleteFetcher = async (
    query: string,
    options: {
      limit?: number;
      userId: string;
      minLevel?: number;
      parentIds?: string[];
      excludedCustomers?: string[];
    },
  ) => {
    const response = await callCoreApi<
      ISuccessApiResponse<IUserCustomerPreview[]>
    >(
      `/tenants/api/wtb/tenants/${tenantCode}/users/${
        options.userId
      }/customers/list?${serialize({
        minLevel: options.minLevel,
        parentIds: options.parentIds,
        searchValue: query,
        limit: options.limit,
      })}`,
    );

    return response.data
      .filter((customer) => {
        return !options.excludedCustomers?.includes(customer.id);
      })
      .map((customer) => {
        return {
          label: customer.name,
          value: customer.id,
          level: customer.level,
          code: customer.code,
        };
      });
  };

  const getCustomersByUserQuery = useQuery({
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getCustomersByUser,
    queryKey: ConnectQueryKeys.users.customersByUser(
      options.getCustomersByUser?.args.id as string,
      options.getCustomersByUser?.args.minLevel as number,
      options.getCustomersByUser?.args.parentIds as string[],
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IUserCustomerPreview[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${
          options.getCustomersByUser?.args.id
        }/customers/list?${serialize({
          minLevel: options.getCustomersByUser?.args.minLevel,
          parentIds: options.getCustomersByUser?.args.parentIds,
        })}`,
      ),
  });

  const getContainerEligibleCustomersByUserQuery = useQuery({
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getContainerEligibleCustomersByUser,
    queryKey: ConnectQueryKeys.users.containerEligibleCustomersByUser(
      options.getContainerEligibleCustomersByUser?.args.id as string,
      options.getContainerEligibleCustomersByUser?.args.minLevel as number,
      options.getContainerEligibleCustomersByUser?.args.parentIds as string[],
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IUserCustomerPreview[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${
          options.getContainerEligibleCustomersByUser?.args.id
        }/customers/container-eligible?${serialize({
          minLevel: options.getContainerEligibleCustomersByUser?.args.minLevel,
          parentIds:
            options.getContainerEligibleCustomersByUser?.args.parentIds,
        })}`,
      ),
  });

  const acceptCookieConsentMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${variables.id}/accept-cookies-consent`,
        {
          body: JSON.stringify(variables),
          method: 'PATCH',
        },
      ),
    ...options.acceptCookieConsent,
    keysToInvalidateOnSuccess: (variable) => [
      CommonQueryKeys.appContext(variable.id),
    ],
  });
  const acceptTermsOfUseMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${variables.id}/accept-terms-of-use`,
        {
          body: JSON.stringify(variables),
          method: 'PATCH',
        },
      ),
    ...options.acceptTermsOfUse,
    keysToInvalidateOnSuccess: (variable) => [
      CommonQueryKeys.appContext(variable.id),
    ],
  });

  const unlockUserMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${variables.id}/unlock`,
        {
          method: 'PATCH',
        },
      ),
    ...options.unlockUser,
    keysToInvalidateOnSuccess: (variables) => [
      ConnectQueryKeys.users.detail(variables.id),
    ],
  });

  const updateLocaleMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${variables.id}/locale`,
        {
          body: JSON.stringify(variables),
          method: 'PATCH',
        },
      ),
    ...options.updateLocale,
    keysToInvalidateOnSuccess: (variable) => [
      CommonQueryKeys.appContext(variable.locale),
    ],
  });

  const getUserCustomerById = useQuery({
    refetchOnWindowFocus: false,
    enabled: options.getUserCustomerById?.enabled ?? false,
    ...options.getUserCustomerById,
    queryKey: ConnectQueryKeys.users.customerByUser(
      options.getUserCustomerById?.args.userId as string,
      options.getUserCustomerById?.args.customerId as string,
    ),
    queryFn: () => {
      const { userId, customerId } = options.getUserCustomerById!.args;
      return callCoreApi<ISuccessApiResponse<IUserCustomer>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${userId}/customers/${customerId}`,
      );
    },
  });

  const paginateCustomersByUser = useQuery({
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.paginateCustomersByUser,
    queryKey: ConnectQueryKeys.users.paginateCustomersByUser(
      options.paginateCustomersByUser?.args.userId as string,
      options.paginateCustomersByUser?.args.ids as string[],
    ),
    queryFn: () =>
      callCoreApi<IPaginationResponse<IUserCustomer>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${
          options.getCustomersByUser?.args.id
        }/customers?${serialize({
          limit: PAGINATE_ALL_LIMIT,
          ids: options.paginateCustomersByUser?.args.ids,
        })}`,
      ),
  });

  const bulkPatchMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/users`,
        {
          method: 'PATCH',
          body: JSON.stringify(variables),
        },
      ),
    ...options.bulkPatch,
    keysToInvalidateOnSuccess: [ConnectQueryKeys.users.base()],
  });

  const listSupplierByUserQuery = useQuery({
    refetchOnWindowFocus: false,
    ...options.listSupplierByUser,
    enabled:
      Boolean(options.listSupplierByUser?.enabled) &&
      Boolean(options.listSupplierByUser?.args.userId),
    queryKey: ConnectQueryKeys.users.listSupplierByUser(
      options.listSupplierByUser?.args.userId as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IContainerSupplier>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/users/${options.listSupplierByUser?.args.userId as string}/container-suppliers`,
      ),
  });
  return restrictApi(options, {
    ...api,
    getGroups: getGroups,
    getCustomersByUser: getCustomersByUserQuery,
    customersByUserAutocomplete: customersByUserAutocompleteFetcher,
    getContainerEligibleCustomersByUser:
      getContainerEligibleCustomersByUserQuery,
    acceptCookieConsent: acceptCookieConsentMutation,
    acceptTermsOfUse: acceptTermsOfUseMutation,
    unlockUser: unlockUserMutation,
    updateLocale: updateLocaleMutation,
    getUserCustomerById,
    paginateCustomersByUser,
    bulkPatch: bulkPatchMutation,
    listSupplierByUser: listSupplierByUserQuery,
  });
};
